import { createStyles, makeStyles } from '@mui/styles';
import { useStateRef } from 'copyright-clearance-center-common-ui-react';
import React, { useEffect, useRef } from 'react';

const useStyles = makeStyles(() =>
	createStyles({
		container: {
			display: 'flex',
			alignItems: 'center',
			gap: '0.25rem',
			flexWrap: 'wrap'
		}
	})
);

export interface IWrappedBoxProps {
	rowsCount: number;
	moreButtonRef: React.MutableRefObject<HTMLButtonElement> | React.MutableRefObject<HTMLDivElement>;
	lessButtonRef: React.MutableRefObject<HTMLButtonElement> | React.MutableRefObject<HTMLDivElement>;
	lastChildrenDivRef: React.MutableRefObject<HTMLDivElement>;
	onDisplayedCountChange?: (expandedCount: number) => void;
	onExpand?: (expanded: boolean) => void;
	containerStyles?: React.CSSProperties;
}

const WrappedBox: React.FunctionComponent<IWrappedBoxProps> = props => {
	const classes = useStyles();
	const rootRef = useRef<HTMLDivElement>(null);
	const [expanded, setExpanded, expandedRef] = useStateRef(false);

	useEffect(() => {
		props.moreButtonRef.current.onclick = e => {
			e.stopPropagation();
			setExpanded(true);

			if (props.onExpand) {
				props.onExpand(true);
			}
		};

		props.lessButtonRef.current.onclick = e => {
			e.stopPropagation();
			setExpanded(false);

			if (props.onExpand) {
				props.onExpand(false);
			}
		};
	}, []);

	const toggleDisplaying = (items: HTMLElement[], display: boolean) => {
		items.forEach(i => (i.style.display = display ? 'flex' : 'none'));
	};

	const getRowOffset = (row: number, items: HTMLElement[]) => {
		let topOffset = items[0]?.offsetTop;
		let rowIndex = 0;

		for (const item of items) {
			if (item.offsetTop > topOffset) {
				rowIndex++;
				topOffset = item.offsetTop;
			}

			if (rowIndex === row - 1) {
				return topOffset;
			}
		}
	};

	const updateDisplayedItems = () => {
		const itemsBlock = [...rootRef.current.children]
			.slice(0, rootRef.current.children.length)
			.map(b => b as HTMLElement);

		toggleDisplaying(itemsBlock, true);

		if (expandedRef.current || itemsBlock.length === 0) {
			toggleDisplaying([props.moreButtonRef.current], false);
			toggleDisplaying([props.lessButtonRef.current], true);
			return;
		}

		toggleDisplaying([props.moreButtonRef.current], true);
		toggleDisplaying([props.lessButtonRef.current], false);

		let displayedItemsCount = itemsBlock.length - 1;
		const lastRowOffset = getRowOffset(props.rowsCount, itemsBlock);

		if (itemsBlock[itemsBlock.length - 1].offsetTop > lastRowOffset) {
			while (props.lastChildrenDivRef.current.offsetTop > lastRowOffset) {
				displayedItemsCount--;
				toggleDisplaying([itemsBlock[displayedItemsCount]], false);
			}
		}

		if (displayedItemsCount === itemsBlock.length - 1) {
			toggleDisplaying([props.moreButtonRef.current], false);
		}

		if (props.onDisplayedCountChange) {
			props.onDisplayedCountChange(displayedItemsCount);
		}
	};

	useEffect(() => {
		const resizeObserver = new ResizeObserver(e => {
			if (e[0].contentRect.width > 0) {
				updateDisplayedItems();
			}
		});

		resizeObserver.observe(rootRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	useEffect(() => {
		updateDisplayedItems();
	}, [expanded, props.children]);

	return (
		<div ref={rootRef} style={props.containerStyles} className={classes.container}>
			{props.children}
		</div>
	);
};
export default WrappedBox;
